import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { RadioRating } from "~/common/components/ui/RadioRating";
import TextArea from "~/common/components/ui/Textarea";
import { AISummaryResponseQuestionData } from "~/common/utils";
import { CeroToFive } from "~/common/utils/consultations/screening/screeningTypes";
import { ConsultationQuestionsLayout } from "~/patients/components/consultations/shared";
import {
  AISummaryResponseFormValues,
  AISummaryResponseSchema,
  AISummaryResponseValuesSchemaFormValues,
} from "~/patients/utils/consultations/intakes";

interface AIResponseSummaryForm {
  question: AISummaryResponseQuestionData;
  handleGoBack: () => void;
  onSubmit: (answer: AISummaryResponseFormValues) => void;
  isSubmitting: boolean;
  disableBack: boolean;
  completedAnswers: AISummaryResponseValuesSchemaFormValues;
  options?: string[];
}

export const YourAIResponse = ({
  completedAnswers,
  disableBack,
  handleGoBack,
  isSubmitting,
  onSubmit,
  options,
}: AIResponseSummaryForm) => {
  const aiResponse = options ? options[0] : "";
  const {
    handleSubmit,
    register,
    watch,
    control,
    formState: { errors },
  } = useForm<AISummaryResponseFormValues>({
    defaultValues: {
      answer: {
        modifications: completedAnswers?.modifications ?? "",
        value: completedAnswers?.value ?? "",
      },
    },
    resolver: zodResolver(AISummaryResponseSchema),
  });

  const saveAnswers: SubmitHandler<AISummaryResponseFormValues> = (data) => {
    onSubmit({
      answer: {
        modifications: data.answer?.modifications ?? "",
        value: data.answer?.value ?? "",
      },
    });
  };

  return (
    <ConsultationQuestionsLayout
      handleNext={handleSubmit(saveAnswers)}
      handleBack={handleGoBack}
      isSubmitting={isSubmitting}
      disableBack={disableBack}
    >
      <div className="mb-6 flex flex-col gap-2.5 rounded-md border border-primary-300 bg-primary-100 px-4 py-3">
        <p className="text-lg font-medium leading-5 text-gray-70 first-letter:capitalize">
          Your AI response
        </p>
        <p className="text-gray-30">{aiResponse}</p>
      </div>

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <p className="text-lg font-medium leading-5 first-letter:capitalize">
              On a scale of 0 to 5, how well does this summary describe you?
            </p>
            <p className="italic">0 (not at all)- 5 (completely)</p>
          </div>
          <Controller
            control={control}
            name="answer.value"
            render={({ field }) => (
              <RadioRating
                options={CeroToFive.map((option) => ({
                  value: option,
                  label: option,
                }))}
                lowerRateLevel="not at all"
                highestRateLevel="completely"
                error={errors.answer?.value?.message}
                {...field}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-4">
          <p className="text-lg font-medium leading-5 first-letter:capitalize">
            How would you modify it?
          </p>
          <TextArea
            rows={5}
            placeholder="Please describe further"
            id="how_would_you_modify_it"
            error={errors?.answer?.modifications?.message}
            className="placeholder:not-italic"
            charactersCount={watch("answer.modifications")?.length ?? 0}
            maxCharacters={5000}
            {...register("answer.modifications")}
          />
        </div>
      </div>
    </ConsultationQuestionsLayout>
  );
};
